import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DialogFooter } from "@/components/ui/dialog";
import { SUPPORTED_CURRENCIES } from "@/components/etf-form/etfForm.schema";
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

const etfDividendFormSchema = z.object({
  etfHoldingId: z.string().min(1, "ETF holding is required"),
  paymentDate: z.string().min(1, "Payment date is required"),
  amountPerUnit: z
    .number({ required_error: "Amount per unit is required" })
    .positive("Amount per unit must be greater than zero"),
  unitsHeldAtPayment: z
    .number({ required_error: "Units held at payment is required" })
    .positive("Units held at payment must be greater than zero"),
  currency: z.enum(SUPPORTED_CURRENCIES, { required_error: "Currency is required" }),
  notes: z.string().optional(),
});

type ETFDividendFormValues = z.infer<typeof etfDividendFormSchema>;

interface ETFDividendFormProps {
  holdings: ETFHolding[];
  preselectedHoldingId?: string;
  onSave: (data: ETFDividendCreateRequest) => void | Promise<void>;
  onCancel: () => void;
}

export function ETFDividendForm({
  holdings,
  preselectedHoldingId,
  onSave,
  onCancel,
}: ETFDividendFormProps) {
  const form = useForm<ETFDividendFormValues>({
    resolver: zodResolver(etfDividendFormSchema),
    defaultValues: {
      etfHoldingId: preselectedHoldingId ?? "",
      paymentDate: "",
      amountPerUnit: undefined,
      unitsHeldAtPayment: undefined,
      currency: undefined,
      notes: "",
    },
  });

  const onSubmit = async (values: ETFDividendFormValues) => {
    await onSave(values as ETFDividendCreateRequest);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label="Record ETF dividend"
      >
        <FormField
          control={form.control}
          name="etfHoldingId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ETF Holding *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger aria-required="true" aria-label="Select ETF holding">
                    <SelectValue placeholder="Select ETF holding" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {holdings.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.ticker_symbol} — {h.etf_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="paymentDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Date *</FormLabel>
              <FormControl>
                <Input type="date" aria-required="true" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="amountPerUnit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount per Unit *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="e.g. 1.50"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unitsHeldAtPayment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Units Held at Payment *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.000001"
                    min="0"
                    placeholder="e.g. 10.5"
                    aria-required="true"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? undefined : parseFloat(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger aria-required="true" aria-label="Select currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Optional notes about this dividend payment"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Record Dividend</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
